import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FileCode,
  FileText,
  ArrowUpRight,
  FileSearch,
  FileBarChart2,
  HelpCircle,
  ScrollText,
  FolderGit2,
} from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';
import { useAppContext } from '../../contexts/useAppContext';

interface DocumentsGridProps {
  data: RepositoryAnalysis;
}

const DOC_ICONS: Record<string, React.FC<{ className?: string }>> = {
  FINDINGS: FileSearch,
  EVIDENCE: FileBarChart2,
  SUMMARY: ScrollText,
  OPEN_QUESTIONS: HelpCircle,
};

const formatDocTitle = (title: string): string =>
  title
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/** Skip the first H1 (it just repeats the doc title) and cap preview length. */
const getPreviewContent = (content: string): string => {
  const withoutFirstH1 = content.replace(/^#\s+[^\n]+\n?/, '').trimStart();
  return withoutFirstH1.length > 300 ? withoutFirstH1.substring(0, 297) + '…' : withoutFirstH1;
};

/** Downscaled markdown components so headings fit inside a small card. */
const previewComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <p className="font-semibold text-xs text-primary mb-1">{children}</p>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <p className="font-semibold text-xs text-primary mb-1">{children}</p>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <p className="font-medium text-xs text-primary mb-0.5">{children}</p>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <p className="font-medium text-xs text-secondary mb-0.5">{children}</p>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="text-xs text-secondary leading-relaxed mb-1">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="text-xs text-secondary space-y-0.5 mb-1 list-disc list-inside">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="text-xs text-secondary space-y-0.5 mb-1 list-decimal list-inside">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-primary">{children}</strong>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="font-mono bg-slate-100 dark:bg-slate-700/50 px-0.5 rounded text-[10px]">
      {children}
    </code>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-2 border-slate-300 dark:border-slate-600 pl-2 text-secondary italic">
      {children}
    </blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-hidden my-1.5 rounded border border-slate-200 dark:border-slate-700">
      <table className="min-w-full border-collapse text-[11px]">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-slate-100 dark:bg-slate-700/60">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => <tr>{children}</tr>,
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-2 py-1 text-left font-semibold text-primary border-r border-slate-200 dark:border-slate-700 last:border-r-0 whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-2 py-1 text-secondary border-r border-slate-200 dark:border-slate-700 last:border-r-0">
      {children}
    </td>
  ),
};

// Temporarily hidden from the grid; may be reinstated later. Filtered by fileName
// (the stable identifier the transformer sets), not the display title.
const HIDDEN_DOC_FILENAMES = new Set(['ai-docs/report.md']);

export const DocumentsGrid: React.FC<DocumentsGridProps> = ({ data }) => {
  const { jobId } = useParams<{ jobId: string }>();
  const { isDemoMode } = useAppContext();
  const documents = data?.documents || [];

  // Keep the original index for the detail URL; DocumentViewPage indexes the full array.
  const visibleDocuments = documents
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => !HIDDEN_DOC_FILENAMES.has(doc.fileName));

  if (visibleDocuments.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
            <FileCode className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-primary">Analysis Documents</h2>
            {data.projectPath ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <FolderGit2 className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                <span className="text-sm text-teal-600 dark:text-teal-400 font-mono">
                  {data.projectPath}
                </span>
              </div>
            ) : (
              <p className="text-sm text-secondary">Generated documentation and reports</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visibleDocuments.map(({ doc, index }) => {
          const isDriftDoc = doc.fileName === 'maintenance-health-report.md';
          // Strip ":projectPath" suffix that irisV3Transformer appends to project repositoryIds
          const repoId = data.repositoryId?.split(':')[0];
          const baseUrl =
            jobId && repoId ? `/repository/${repoId}/analysis/${jobId}/docs/${index}` : '#';

          const primaryLanguage = data.languages?.[0];
          const languageStr =
            typeof primaryLanguage === 'string' ? primaryLanguage : primaryLanguage?.name;
          const driftParams = new URLSearchParams({
            type: 'drift',
            ...(data.repositoryName && { repoName: data.repositoryName }),
            ...(languageStr && { language: languageStr }),
          });
          if (isDemoMode) driftParams.set('demo', 'true');
          const projectParam = data.projectPath
            ? `project=${encodeURIComponent(data.projectPath)}`
            : null;

          const appendParam = (base: string, param: string | null) =>
            param ? `${base}${base.includes('?') ? '&' : '?'}${param}` : base;

          const url = appendParam(
            isDriftDoc
              ? `${baseUrl}?${driftParams.toString()}`
              : isDemoMode
                ? `${baseUrl}?demo=true`
                : baseUrl,
            projectParam,
          );

          const Icon = DOC_ICONS[doc.title] ?? FileText;
          const displayTitle = formatDocTitle(doc.title);
          const previewContent = getPreviewContent(doc.content);
          return (
            <Link
              key={index}
              to={url}
              target="_blank"
              className="flex flex-col bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xs hover:shadow-md transition-all duration-200 group border border-slate-200 dark:border-slate-700/50 hover:border-teal-300 dark:hover:border-teal-600/60"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="shrink-0 p-1.5 rounded-md bg-teal-50 dark:bg-teal-900/20 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/40 transition-colors">
                    <Icon className="w-4 h-4 text-teal-500 dark:text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-sm text-primary group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors leading-tight">
                    {displayTitle}
                  </h3>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-secondary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
              </div>

              {/* Markdown preview */}
              <div className="grow max-h-48 overflow-hidden">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={previewComponents as object}>
                  {previewContent}
                </ReactMarkdown>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentsGrid;
